import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Leaf, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';

const VALUES = [
  {
    icon: Heart,
    title: '熱愛',
    description: '對品質的堅持，是我們一切的起點。',
    bgKey: 'story.values.background1',
    defaultBg: '/media/media/story-values-bg-1.jpg',
  },
  {
    icon: Shield,
    title: '品質',
    description: '匯聚世界香辣食材、嚴選原料，每一款辣粉都有品質保證。',
    bgKey: 'story.values.background2',
    defaultBg: '/media/media/story-values-bg-2.jpg',
  },
  {
    icon: Leaf,
    title: '純粹',
    description: '沒有多餘添加，讓食材風味自然呈現。',
    bgKey: 'story.values.background3',
    defaultBg: '/media/media/story-values-bg-3.jpg',
  },
];

export default function Story() {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    trackPageView('/story');
  }, []);

  const { getValue, getHtml, loading: cmsLoading } = useCMS();

  const galleryImages = [
    {
      src: getValue('story.gallery.image1', '/media/media/manufacture-alex-tasting.jpg'),
      caption: getValue('story.gallery.caption1', '親自試味，確保每一批都夠辣夠香'),
      span: 'md:col-span-2 md:row-span-2',
    },
    {
      src: getValue('story.gallery.image2', '/media/media/manufacture-chili-selection.jpg'),
      caption: getValue('story.gallery.caption2', '嚴選世界各地優質原料'),
      span: '',
    },
    {
      src: getValue('story.gallery.image3', '/media/media/manufacture-grinding-process.jpg'),
      caption: getValue('story.gallery.caption3', '精心製作，保留原味'),
      span: '',
    },
    {
      src: getValue('story.gallery.image4', '/media/media/manufacture-partner-tasting.jpg'),
      caption: getValue('story.gallery.caption4', '與夥伴反覆試味，找到最適合香港人的比例'),
      span: '',
    },
    {
      src: getValue('story.gallery.image5', '/media/media/manufacture-jars-finished.jpg'),
      caption: getValue('story.gallery.caption5', '手工封罐，即製即送'),
      span: 'md:col-span-2',
    },
  ];

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white py-20 md:py-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/media/media/1786030012_hkgspicyvalley_bg.jpeg')" }}
        />
        <div className="absolute inset-0 bg-x1493-dark/70" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <p className="text-x1493-secondary font-bold tracking-widest mb-4">品牌故事</p>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            dangerouslySetInnerHTML={getHtml('story.hero.title', '我們的品牌故事')}
          />
          <p
            className="text-lg text-white/80 leading-relaxed max-w-2xl mx-auto"
            dangerouslySetInnerHTML={getHtml('story.hero.subtitle', '每款產品都經過精心挑選與調配，每一步親自把關——不是流水線標準化產品，而是有溫度的手作。（後台 CMS 可編輯）')}
          />
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              dangerouslySetInnerHTML={getHtml('story.gallery.title', '從原料到成品，每一步都看得見')}
            />
            <p
              className="text-stone-600 max-w-2xl mx-auto"
              dangerouslySetInnerHTML={getHtml('story.gallery.description', '我們堅持小批量製作，讓每一罐辣粉都帶著手作的溫度與 Alex 的堅持。')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[200px]">
            {galleryImages.map((item, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setLightboxImage(item.src)}
                className={`relative rounded-2xl overflow-hidden group text-left ${item.span || ''}`}
              >
                <img
                  src={item.src}
                  alt={item.caption}
                  className="absolute inset-0 w-full h-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300">
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
                    點擊放大
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <p className="text-white font-medium text-sm md:text-base leading-snug">
                    {item.caption}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Lightbox */}
          {lightboxImage && (
            <div
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              onClick={() => setLightboxImage(null)}
            >
              <button
                type="button"
                onClick={() => setLightboxImage(null)}
                className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
                aria-label="關閉"
              >
                <X size={32} />
              </button>
              <img
                src={lightboxImage}
                alt="放大預覽"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      </section>

      {/* Brand Values */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              dangerouslySetInnerHTML={getHtml('story.values.title', '我們的堅持')}
            />
            <p
              className="text-stone-600"
              dangerouslySetInnerHTML={getHtml('story.values.subtitle', '一份好的辣，來自對細節的執著。')}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {VALUES.map(({ icon: Icon, title, description, bgKey, defaultBg }) => (
              <div
                key={title}
                className="relative rounded-2xl p-8 overflow-hidden min-h-[320px] flex flex-col group"
              >
                <img
                  src={getValue(bgKey, defaultBg)}
                  alt={title}
                  className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/40" />
                <div className="relative z-10 flex flex-col h-full">
                  <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="text-x1493-secondary" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
                  <p className="text-white/80">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-x1493-primary text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            dangerouslySetInnerHTML={getHtml('story.cta.title', '準備好開始了嗎？')}
          />
          <p
            className="text-white/90 mb-8"
            dangerouslySetInnerHTML={getHtml('story.cta.subtitle', '探索我們的辣粉系列，找出屬於你的辣度。')}
          />
          <Link to="/store" className="inline-flex items-center gap-2 bg-white text-x1493-primary font-bold px-8 py-4 rounded-2xl hover:bg-stone-100 transition">
            {getValue('story.cta.buttonText', '逛商店')}
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
